import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button.tsx';

const SUSPENSION_REASONS = [
  {
    value: 'INAPPROPRIATE_CONTENT',
    label: 'Contenu inapproprié',
    description: 'Ton compte a été suspendu en raison de contenus inappropriés partagés sur la plateforme.',
  },
  {
    value: 'HARASSMENT',
    label: 'Harcèlement',
    description: 'Ton compte a été suspendu en raison de comportements de harcèlement envers d\'autres utilisateurs.',
  },
  {
    value: 'SPAM',
    label: 'Spam / Faux profil',
    description: 'Ton compte a été suspendu en raison de comportements de spam ou d\'un profil non authentique.',
  },
  {
    value: 'POLICY_VIOLATION',
    label: 'Violation des CGU',
    description: 'Ton compte a été suspendu en raison d\'une violation des conditions générales d\'utilisation.',
  },
  {
    value: 'OTHER',
    label: 'Autre',
    description: 'Ton compte a été suspendu suite à une décision de notre équipe de modération.',
  },
] as const;

const CUSTOM_DURATION_VALUE = 'custom';

const DURATION_OPTIONS = [
  { value: 1, label: '1 jour' },
  { value: 3, label: '3 jours' },
  { value: 7, label: '7 jours' },
  { value: 14, label: '14 jours' },
  { value: 30, label: '30 jours' },
  { value: CUSTOM_DURATION_VALUE, label: 'Personnalisé' },
] as const;

interface ISuspendUserModalProps {
  open: boolean;
  onConfirm: (reason: string, durationDays: number) => void;
  onCancel: () => void;
}

const SuspendUserModal = ({ open, onConfirm, onCancel }: ISuspendUserModalProps) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [customDays, setCustomDays] = useState<string>('');

  const isCustomDuration = selectedDuration === CUSTOM_DURATION_VALUE;
  const customDaysNumber = parseInt(customDays, 10);
  const isCustomDaysValid = !isCustomDuration || (Number.isInteger(customDaysNumber) && customDaysNumber >= 1 && customDaysNumber <= 365);
  const resolvedDuration = isCustomDuration ? customDaysNumber : Number(selectedDuration);
  const canConfirm = !!selectedReason && !!selectedDuration && isCustomDaysValid;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(selectedReason, resolvedDuration);
    setSelectedReason('');
    setSelectedDuration('');
    setCustomDays('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setSelectedDuration('');
    setCustomDays('');
    onCancel();
  };

  const selectedReasonData = SUSPENSION_REASONS.find(r => r.value === selectedReason);

  return (
    <Dialog open={open} onOpenChange={isOpen => { if (!isOpen) handleCancel(); }}>
      <DialogContent className={'rounded-2xl p-6 max-w-md mx-auto'}>
        <DialogHeader className={'space-y-1'}>
          <DialogTitle className={'text-lg font-semibold'}>Suspendre l'utilisateur</DialogTitle>
        </DialogHeader>

        <div className={'space-y-4 py-2'}>
          <div className={'space-y-1'}>
            <label className={'text-sm font-medium'}>Message de suspension</label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Choisir un message...'} />
              </SelectTrigger>
              <SelectContent>
                {SUSPENSION_REASONS.map(reason => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedReasonData && (
            <p className={'text-xs text-muted-foreground bg-muted rounded-md p-3 italic'}>
              Message vu par l'utilisateur : « {selectedReasonData.description} »
            </p>
          )}

          <div className={'space-y-1'}>
            <label className={'text-sm font-medium'}>Durée de la suspension</label>
            <Select value={selectedDuration} onValueChange={setSelectedDuration}>
              <SelectTrigger className={'w-full'}>
                <SelectValue placeholder={'Choisir une durée...'} />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isCustomDuration && (
              <div className={'flex flex-col gap-1 pt-1'}>
                <input
                  type={'number'}
                  min={1}
                  max={365}
                  value={customDays}
                  onChange={e => setCustomDays(e.target.value)}
                  placeholder={'Nombre de jours (1-365)'}
                  className={'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring'}
                />
                {customDays && !isCustomDaysValid && (
                  <span className={'text-xs text-destructive'}>Veuillez entrer un nombre entre 1 et 365.</span>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className={'flex flex-row gap-3 pt-2'}>
          <Button className={'flex-1'} onClick={handleCancel} variant={'secondary'}>
            Annuler
          </Button>
          <Button
            className={'flex-1'}
            onClick={handleConfirm}
            variant={'destructive'}
            disabled={!canConfirm}
          >
            Suspendre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuspendUserModal;
